import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  AccountDto,
  CreateTreasuryTransferRequest,
  TreasuryTransferDto,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canRecord: boolean;
  onOpenLedger: (accountCode: string) => void;
  onSessionLost: () => void;
}

const CASH_SUBLEDGERS = new Set(['cashbox', 'bank_or_wallet']);

const ISSUE_LABELS: Record<string, string> = {
  TRANSFER_ACCOUNT: 'الحسابان يجب أن يكونا خزينة أو بنكًا أو محفظة',
  CASHBOX_NEGATIVE: 'رصيد الخزينة المصدر لا يكفى',
  CASHBOX_NOT_CONFIGURED: 'لا خزينة معرّفة على هذا الحساب',
  CASHBOX_AMBIGUOUS: 'للحساب أكثر من خزينة نشطة — راجع «الخزائن والبنوك»',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/**
 * الخزينة: تحويل بين خزينة وبنك ومحفظة، وقائمة التحويلات. التحويل لا إيراد ولا مصروف،
 * والخزينة المصدر لا تحوّل أكثر من رصيدها. رصيد كل حساب من كشفه.
 */
export default function Treasury({
  companyId,
  currency,
  accounts,
  canRecord,
  onOpenLedger,
  onSessionLost,
}: Props) {
  const [transfers, setTransfers] = useState<TreasuryTransferDto[] | null>(null);
  const [transferDate, setTransferDate] = useState(today);
  const [fromAccountCode, setFromAccountCode] = useState('');
  const [toAccountCode, setToAccountCode] = useState('');
  const [amount, setAmount] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<TreasuryTransferDto | null>(null);

  const cashAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          account.subledger !== null &&
          CASH_SUBLEDGERS.has(account.subledger),
      ),
    [accounts],
  );
  const nameOf = (code: string) =>
    cashAccounts.find((account) => account.code === code)?.nameAr ?? code;

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setFailure(
          issues.length
            ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ')
            : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api.treasuryTransfers(companyId).then(setTransfers).catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setFailure(null);
    setRecorded(null);
  }

  const parsed = parseAmount(amount);
  const ready =
    fromAccountCode !== '' &&
    toAccountCode !== '' &&
    fromAccountCode !== toAccountCode &&
    parsed !== null &&
    parsed !== '0';

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || parsed === null) return;
    setSubmitting(true);
    setFailure(null);
    const body: CreateTreasuryTransferRequest = {
      transferDate,
      fromAccountCode,
      toAccountCode,
      amount: parsed,
      ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
    };
    try {
      const transfer = await api.createTreasuryTransfer(companyId, idempotencyKey, body);
      setRecorded(transfer);
      setAmount('');
      setExternalReference('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const accountSelect = (value: string, onChange: (code: string) => void, placeholder: string) => (
    <select
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
        touched();
      }}
    >
      <option value="">{placeholder}</option>
      {cashAccounts.map((account) => (
        <option key={account.code} value={account.code}>
          {account.code} — {account.nameAr}
        </option>
      ))}
    </select>
  );

  return (
    <>
      {canRecord && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>تحويل بين الخزائن والبنوك</h2>
          <div className="form-row">
            <label className="grow">
              من
              {accountSelect(fromAccountCode, setFromAccountCode, '— الحساب المصدر —')}
              <Hint>الخزينة أو البنك الذى تخرج منه الفلوس.</Hint>
            </label>
            <label className="grow">
              إلى
              {accountSelect(toAccountCode, setToAccountCode, '— الحساب المستلم —')}
              <Hint>الخزينة أو البنك الذى تدخل فيه.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              التاريخ
              <input
                type="date"
                value={transferDate}
                onChange={(event) => {
                  setTransferDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label>
              المبلغ
              <input
                inputMode="decimal"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  touched();
                }}
              />
            </label>
            <label className="grow">
              مرجع (إيصال إيداع/سحب)
              <input
                value={externalReference}
                maxLength={64}
                onChange={(event) => {
                  setExternalReference(event.target.value);
                  touched();
                }}
              />
              <Hint>رقم إيصال الإيداع أو السحب من البنك.</Hint>
            </label>
          </div>
          {fromAccountCode !== '' && fromAccountCode === toAccountCode && (
            <p className="error">المصدر والمستلم حساب واحد.</p>
          )}
          {failure && <p className="error">{failure}</p>}
          {recorded && (
            <p className="sub">
              سُجل التحويل {recorded.number} بمبلغ {formatMoney(recorded.amount)} {currency} — قيده{' '}
              {recorded.journalNumber}.
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى التسجيل…' : 'تسجيل التحويل'}
            </button>
            <Hint block>
              ينقل المبلغ بين خزينتين أو بنكين — ليس دخلاً ولا مصروفًا، ولا يغيّر إجمالى فلوسك.
            </Hint>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>التحويلات</h2>
        {transfers === null && !failure && <Loading />}
        {transfers?.length === 0 && <p className="sub">لا تحويلات بعد.</p>}
        {transfers && transfers.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>التاريخ</th>
                  <th>من</th>
                  <th>إلى</th>
                  <th>المبلغ</th>
                  <th>القيد</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer) => (
                  <tr key={transfer.id}>
                    <td className="mono">{transfer.number}</td>
                    <td>{transfer.transferDate}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpenLedger(transfer.fromAccountCode)}
                      >
                        {nameOf(transfer.fromAccountCode)}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpenLedger(transfer.toAccountCode)}
                      >
                        {nameOf(transfer.toAccountCode)}
                      </button>
                    </td>
                    <td className="num-cell">{formatMoney(transfer.amount)}</td>
                    <td className="mono">{transfer.journalNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
